import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, CreditCard } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SectionHeader from "@/components/ui/SectionHeader";

const ratesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Photography & Videography Studio Rental",
  provider: {
    "@type": "LocalBusiness",
    name: "MyStudio",
    url: "https://bookmystudiospace.com",
    telephone: "+1-706-478-7640",
  },
  areaServed: { "@type": "City", name: "Columbus, GA" },
  url: "https://bookmystudiospace.com/rates",
  offers: [
    { "@type": "Offer", name: "Studio A Hourly Rental", price: "45.00", priceCurrency: "USD", url: "https://bookmystudiospace.com/book?studio=studio-a" },
    { "@type": "Offer", name: "Studio B Hourly Rental", price: "70.00", priceCurrency: "USD", url: "https://bookmystudiospace.com/book?studio=studio-b" },
  ],
};

const RatesPage = () => (
  <div className="pt-20">
    <Helmet>
      <title>Studio Rates | MyStudio Columbus, GA</title>
      <meta name="description" content="MyStudio rental rates: Studio A from $45/hr, Studio B from $70/hr. Add-ons and in-house production services available." />
      <link rel="canonical" href="https://bookmystudiospace.com/rates" />
      <script type="application/ld+json">{JSON.stringify(ratesSchema)}</script>
    </Helmet>
    <section className="py-20 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">STUDIO RATES</h1>
        <p className="text-muted-foreground font-body text-lg">Transparent pricing. No hidden fees.</p>
      </div>
    </section>

    {/* Studio Rates */}
    <section className="pb-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[
            { name: "Studio A", rate: "$45", min: "1 hour", features: ["Seamless paper backdrops", "Professional lighting kits", "Tether station & monitor", "Private changing area", "High-speed WiFi", "Bluetooth speaker"] },
            { name: "Studio B", rate: "$70", min: "2 hours", features: ["Full cyclorama wall", "Green screen available", "Film-grade continuous lighting", "4K-ready production setup", "Audio dampening", "Dedicated power circuits"] },
          ].map((s) => (
            <div key={s.name} className="bg-panel border border-border p-8 text-center">
              <h3 className="text-2xl font-display tracking-widest mb-2">{s.name}</h3>
              <p className="text-4xl font-display text-primary mb-1">{s.rate}<span className="text-lg text-muted-foreground">/hr</span></p>
              <p className="text-xs font-mono text-muted-foreground mb-6">Minimum: {s.min}</p>
              <ul className="space-y-2 text-left mb-8">
                {s.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm font-body">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to={`/book?studio=${s.name.toLowerCase().replace(" ", "-")}`}>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold">
                  Book {s.name}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Add-ons */}
    <section className="py-16 bg-surface">
      <div className="container mx-auto px-4 max-w-3xl">
        <SectionHeader title="ADD-ONS" />
        <div className="bg-panel border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-display tracking-wider text-sm">Add-On</th>
                <th className="text-right p-4 font-display tracking-wider text-sm">Price</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: "In-house Shoot Facilitator", price: "$50/hr" },
                { name: "On-call Editor", price: "$75/session" },
                { name: "Prop Access", price: "$25/session" },
                { name: "Green Screen Setup (Studio B)", price: "$20/session" },
                { name: "Custom Set Build", price: "Request Quote" },
              ].map((a) => (
                <tr key={a.name} className="border-b border-border last:border-0">
                  <td className="p-4 text-sm font-body">{a.name}</td>
                  <td className="p-4 text-sm font-mono text-primary text-right">{a.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="py-16">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <SectionHeader title="IN-HOUSE SERVICES" subtitle="Our full production services are available for booking separately." />
        <p className="text-2xl font-display text-primary tracking-wider mb-2">STARTING AT $150</p>
        <p className="text-muted-foreground font-body text-sm mb-6">Contact us for custom quotes on photography, video, editing, and restoration.</p>
        <Link to="/contact">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-body font-semibold px-6">
            Contact for Quote
          </Button>
        </Link>
      </div>
    </section>

    {/* Policies */}
    <section className="py-16 bg-surface">
      <div className="container mx-auto px-4 max-w-3xl">
        <SectionHeader title="POLICIES" />
        <div className="space-y-6">
          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-wider mb-2">50% DEPOSIT REQUIRED</h3>
            <p className="text-sm text-muted-foreground font-body">All studio rentals require a 50% deposit at the time of booking. The remaining balance is due upon arrival.</p>
          </div>
          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-wider mb-2">CANCELLATION POLICY</h3>
            <p className="text-sm text-muted-foreground font-body">
              Cancellations 48+ hours in advance: full deposit refund. Within 48 hours: deposit forfeited. No-shows: charged full amount.
            </p>
          </div>
          <div className="bg-panel border border-border p-6 flex items-center gap-4">
            <CreditCard className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-display tracking-wider mb-1">PAYMENT METHODS</h3>
              <p className="text-sm text-muted-foreground font-body">We accept all major credit/debit cards via Stripe secure checkout.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 text-center">
      <h2 className="text-3xl md:text-5xl font-display tracking-widest mb-6">READY TO BOOK?</h2>
      <Link to="/book">
        <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10 text-lg">
          Book Now
        </Button>
      </Link>
    </section>
  </div>
);

export default RatesPage;
